type JsonLdProps = {
  /** One schema object, or several to emit in a single graph script. */
  data: object | object[]
}

/**
 * Renders a JSON-LD structured-data block as a <script type="application/ld+json">.
 *
 * Server component: because every route is an async Server Component under
 * `output: 'export'`, the script is baked into the static HTML at build time —
 * exactly what search crawlers and AI fetchers parse.
 *
 * Multiple nodes are wrapped in a single `@graph` object (one top-level
 * `@context`) rather than emitted as a bare array: a bare `[{...}, {...}]` has
 * no top-level `@context`, which crashes third-party scripts (e.g. GTM tags)
 * that read `data['@context']` off the parsed JSON-LD.
 */
export default function JsonLd({ data }: JsonLdProps) {
  const payload = Array.isArray(data)
    ? { '@context': 'https://schema.org', '@graph': data.map(stripContext) }
    : data

  return (
    <script
      type="application/ld+json"
      // Escape `<` so a stray `</script>` in CMS-derived content (e.g. FAQ
      // answers) can't terminate the tag early — standard JSON-LD hardening.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(payload).replace(/</g, '\\u003c') }}
    />
  )
}

/**
 * Route-level scripts need a host element: Next may detach a bare script before
 * React unmounts the old route, causing React's removeChild cleanup to throw.
 */
export function RouteJsonLd({ data }: JsonLdProps) {
  return (
    <div style={{ display: 'contents' }} data-route-json-ld="">
      <JsonLd data={data} />
    </div>
  )
}

function stripContext(node: object): object {
  const { '@context': _context, ...rest } = node as { '@context'?: unknown }
  return rest
}
